import { Box, Checkbox, Grid, Menu, MenuItem, Tab, Tabs } from '@mui/material';
import SectionHeader from '../../components/SectionHeader';
import SearchToolbar from '../../components/SearchToolbar/SearchToolbar';
import { RGRLFilters } from './RGRL/components/RGRLFilters';
import useFileGenerationRGRL from './RGRL/hooks/useFileGenerationRGRL';
import EmptyTableSection from '../../components/DataTable/EmptyTableSection';
import DataTable from '../../components/DataTable/DataTable';
import { TEST_IDS } from './consts/testIds';
import { columnTitles } from './RGRL/consts/table';
import { renderStateChipSRT } from './RGRL/components/StateChipSRT';
import { useState } from 'react';
import { getDate } from '../../utils/common';
import { SRT_STATES } from './consts/states';
import BackdropComponent from '../../components/Backdrop/BackdropComponent';
import useTranslate, { TranslateFunction } from '../../hooks/useTranslate';
import { UploadErrorInfoAlert } from './RGRL/components/UploadErrorInfoAlert';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { TabPanel } from '../../components/TabPanel';
import { VisitsFilters } from './Visits/components/VIsitsFilters';
import useFileGenerationVisits from './Visits/hooks/useFileGenerationVisits';
import { VisitsTable } from './Visits/components/VisitsTable';
import { useTranslation } from 'react-i18next';
import { ActionsMenu } from '../../components/ActionsMenu/ActionsMenu';
import { setCurrentFileGenerationTab } from '../../redux/slices/srtPresentationsSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import DrawerManager, { DRAWER_ACTION_VALUES } from './drawers';
import { useRoles } from '../../hooks/useRoles';
import useUserAccess from '../../hooks/useUserAccess';
import useRoutePrefix from '../../hooks/useRoutePrefix';
import { getAccessValues } from '../../components/layout/RoutesManager/access';
import SiniestrosFilters from '../Siniestros/components/SiniestrosFilters/SiniestrosFilters';
import SiniestrosTable from '../Siniestros/components/SiniestrosTable';
import useFileGenerationSiniestros, {
    isSeleccionableArchivosSrt,
    SRT_STATUS_OPTIONS_EXPORTABLES
} from './Siniestros/hooks/useFileGenerationSiniestros';
import { ArchivosGeneradosHoy } from './components/ArchivosGeneradosHoy';
import { normalizarIdOrganizacion } from './hooks/useArchivosGeneradosHoy';
import type { TipoContadorArchivos } from './consts/types';
import { useOrganizationFilter } from '../../contexts/OrganizationFilterContext';

export const getPresentationTypeName = (typeName: string, translate: TranslateFunction): string => {
    if (typeName === 'RGRL') {
        return translate('presentationTypeNames.rgrl');
    }

    if (typeName === 'RAR') {
        return translate('presentationTypeNames.rar');
    }

    return '-';
};

export enum TAB_INDEX {
    RGRL = 0,
    VISITS = 1,
    SINIESTROS = 2
}

export type HandleTabChange = (event: React.SyntheticEvent, newValue: TAB_INDEX) => void;

// El contador "Archivos generados hoy" muestra solo el flujo de la tab activa
const TIPO_CONTADOR_POR_TAB: Record<TAB_INDEX, TipoContadorArchivos> = {
    [TAB_INDEX.RGRL]: 'rgrl',
    [TAB_INDEX.VISITS]: 'visitas',
    [TAB_INDEX.SINIESTROS]: 'roam'
};

const RGRLIndex = () => {
    const { t } = useTranslation('filesGenerationSRT');
    const { translate } = useTranslate(t);
    const [open, setOpen] = useState('');
    const [isShowMoreVisitsFiltersOpen, setIsShowMoreVisitsFiltersOpen] = useState(false);
    const [isShowMoreSiniestrosFiltersOpen, setIsShowMoreSiniestrosFiltersOpen] = useState(false);
    const currentTab = useAppSelector((state) => state.srtPresentations.currentTab);

    const handleCloseDrawer = () => {
        setOpen('');
    };
    const dispatch = useAppDispatch();

    const {
        filters,
        tableMenu,
        selectedItemFromAction,
        selected,
        selectedPresentations,
        presentations,
        isFetchingPresentations,
        presentationUploadErrors,
        contractsOptions,
        isFetchingContractsOptions,
        establishmentsOptions,
        isFetchingEstablishmentsOptions,
        // isLoadingRgrl,
        isDownloadingPDF,
        isGeneretingFiles,
        draftStartDate,
        draftEndDate,
        isDownloading,
        resetDateRange,
        handleDateChange,
        setSelectedPresentations,
        handleItemCheck,
        handleClickAction,
        handleRowClick,
        generateAndDownloadFiles,
        handleDownloadPresentation,
        handleExportPresentations,
        isExportingPresentations
    } = useFileGenerationRGRL(handleCloseDrawer);

    const navigate = useNavigate();
    const { isPerfilBackOffice } = useRoles();
    const { selectedOrganization } = useOrganizationFilter();
    const { t: tSiniestros } = useTranslation('siniestrosRoam');
    const routePrefix = useRoutePrefix();
    const FEATURES = getAccessValues(useUserAccess());

    const siniestrosTab = useFileGenerationSiniestros({
        enabled: currentTab === TAB_INDEX.SINIESTROS,
        translate
    });

    const {
        filters: baseFilters,
        extraFilters,
        handleSubmitSearch,
        handleChangeExtraFilters,
        setFilters,
        clearFilters
    } = filters;

    const {
        visitsFilters,
        visitas,
        isFechingVisits,
        isFetchingVisitTypes,
        visitTypesOptions,
        visitsTableMenu,
        selectedVisits,
        setSelectedVisits,
        visitsTableMenuItems,
        selectedItemFromAction: selectedVisitItemFromAction,
        selected: selectedVisitElement,
        isDownloadingVisitActa,
        isGeneratingVisitsFiles,
        handleRowClick: handleVisitRowClick,
        handleClickAction: handleVisitClickAction,
        handleItemCheck: handleVisitItemCheck,
        generateAndDownloadFiles: generateAndDownloadVisitsFiles,
        handleExportVisits,
        isExportingVisits
    } = useFileGenerationVisits(handleCloseDrawer);

    const {
        handleChangeExtraFilters: handleChangeVisitsExtraFilters,
        setFilters: setVisitsFilters,
        extraFilters: visitsExtraFilters,
        handleSubmitSearch: handleSubmitVisitsSearch,
        filters: visitsBaseFilters,
        clearFilters: visitsClearFilters
    } = visitsFilters;

    const { menuOpen, anchorEl, closeMenu } = tableMenu;

    const {
        menuOpen: visitsTableMenuOpen,
        anchorEl: visitsAnchorEl,
        closeMenu: visitsCloseMenu
    } = visitsTableMenu;

    const handleTabChange = (_event: React.SyntheticEvent, newValue: TAB_INDEX) => {
        resetDateRange();
        clearFilters();
        visitsClearFilters();
        siniestrosTab.reset();
        dispatch(setCurrentFileGenerationTab(newValue));
    };

    const handleToggleVisitsMoreFilters = () => {
        if (isShowMoreVisitsFiltersOpen) {
            handleChangeVisitsExtraFilters({ target: { name: 'fechaInicio', value: null } });
            handleChangeVisitsExtraFilters({ target: { name: 'fechaFin', value: null } });
        }
        setIsShowMoreVisitsFiltersOpen((previouslyOpen) => !previouslyOpen);
    };

    const selectableItems =
        presentations?.data?.data?.filter((item: any) => item.estadoSRT !== SRT_STATES.ERROR) || [];

    const isSelectAllEnabled = extraFilters?.idOrganizacion != null;

    const isAllSelected =
        selectableItems.length > 0 &&
        selectableItems.every((item: any) =>
            selectedPresentations.some((selectedPresentation: any) => selectedPresentation.id === item.id)
        );

    const handleSelectAllCheckPress = (checked: boolean) => {
        if (!isSelectAllEnabled) {
            enqueueSnackbar(
                translate('drawers.generateFilesSRT.enqueues.warning.noOrganizationSelected'),
                { variant: 'warning' }
            );
            return;
        }

        if (checked) {
            setSelectedPresentations((previouslySelected: any[]) => [
                ...previouslySelected,
                ...selectableItems.filter(
                    (item: any) =>
                        !previouslySelected.some((selectedPresentation: any) => selectedPresentation.id === item.id)
                )
            ]);
        } else {
            setSelectedPresentations((previouslySelected: any[]) =>
                previouslySelected.filter(
                    (selectedPresentation: any) =>
                        !selectableItems.some((item: any) => item.id === selectedPresentation.id)
                )
            );
        }
    };

    const visitsSelectableItems =
        visitas?.data?.data?.filter((item: any) => item.estadoSRT !== SRT_STATES.ERROR) || [];

    const isVisitsSelectAllEnabled = visitsExtraFilters?.idOrganizacion != null;

    const isAllVisitsSelected =
        visitsSelectableItems.length > 0 &&
        visitsSelectableItems.every((item: any) =>
            selectedVisits.some((selectedVisit: any) => selectedVisit.id === item.id)
        );

    const handleVisitsSelectAllCheckPress = (checked: boolean) => {
        if (!isVisitsSelectAllEnabled) {
            enqueueSnackbar(
                translate('drawers.generateFilesSRT.enqueues.warning.noOrganizationSelected'),
                { variant: 'warning' }
            );
            return;
        }

        if (checked) {
            setSelectedVisits((previouslySelected: any[]) => [
                ...previouslySelected,
                ...visitsSelectableItems.filter(
                    (item: any) =>
                        !previouslySelected.some((selectedVisit: any) => selectedVisit.id === item.id)
                )
            ]);
        } else {
            setSelectedVisits((previouslySelected: any[]) =>
                previouslySelected.filter(
                    (selectedVisit: any) => !visitsSelectableItems.some((item: any) => item.id === selectedVisit.id)
                )
            );
        }
    };

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <SectionHeader>
                        <SectionHeader.Title>{translate('header.title')}</SectionHeader.Title>
                        {/* Botones arriba y, debajo, el contador de archivos generados hoy (solo BackOffice).
                            El header no tiene padding vertical: sin pt los botones quedan pegados al borde. */}
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-end"
                            gap={1}
                            pt={3}
                            pb={2}
                        >
                        <Grid display="flex" direction={'row'} gap={1}>
                            <TabPanel value={currentTab} index={TAB_INDEX.RGRL}>
                                <Grid display="flex" direction={'row'} gap={1}>
                                    {isPerfilBackOffice && (
                                        <SectionHeader.IconHeader
                                            variant="outlined"
                                            text={translate('header.exportExcel')}
                                            showIcon={false}
                                            disabled={isExportingPresentations}
                                            onClick={handleExportPresentations}
                                        />
                                    )}
                                    <SectionHeader.IconHeader
                                        variant="outlined"
                                        text={translate('header.uploadSRTReply')}
                                        showIcon={false}
                                        testID={TEST_IDS.header.uploadSRTReply}
                                        onClick={() =>
                                            setOpen(DRAWER_ACTION_VALUES.UPLOAD_RESPONSE_SRT)
                                        }
                                    />
                                </Grid>
                            </TabPanel>
                            <TabPanel value={currentTab} index={TAB_INDEX.VISITS}>
                                {isPerfilBackOffice && (
                                    <SectionHeader.IconHeader
                                        variant="outlined"
                                        text={translate('header.exportExcel')}
                                        showIcon={false}
                                        disabled={isExportingVisits}
                                        onClick={handleExportVisits}
                                    />
                                )}
                            </TabPanel>
                            <TabPanel value={currentTab} index={TAB_INDEX.SINIESTROS}>
                                {isPerfilBackOffice && (
                                    <SectionHeader.IconHeader
                                        variant="outlined"
                                        text={translate('header.exportExcel')}
                                        showIcon={false}
                                        testID={TEST_IDS.header.exportExcelSiniestros}
                                        disabled={siniestrosTab.isExportandoExcel}
                                        onClick={siniestrosTab.handleExportExcel}
                                    />
                                )}
                            </TabPanel>
                            {/* La tab Siniestros genera directo, sin el drawer, y valida con un único
                                mensaje (6d) */}
                            {currentTab === TAB_INDEX.SINIESTROS ? (
                                <SectionHeader.IconHeader
                                    text={translate('header.generateFile')}
                                    showIcon={false}
                                    testID={TEST_IDS.header.generateFile}
                                    loading={siniestrosTab.isGenerandoArchivo}
                                    onClick={siniestrosTab.handleGenerarArchivo}
                                />
                            ) : (
                                <SectionHeader.IconHeader
                                    text={translate('header.generateFiles')}
                                    showIcon={false}
                                    testID={TEST_IDS.header.generateFiles}
                                    onClick={() => {
                                        if (isPerfilBackOffice && !extraFilters?.idOrganizacion) {
                                            enqueueSnackbar(
                                                translate(
                                                    'drawers.generateFilesSRT.enqueues.warning.noOrganizationSelected'
                                                ),
                                                {
                                                    variant: 'warning'
                                                }
                                            );
                                            return;
                                        }
                                        if (
                                            selectedPresentations?.length <= 0 &&
                                            currentTab === TAB_INDEX.RGRL
                                        ) {
                                            enqueueSnackbar(
                                                translate(
                                                    'drawers.generateFilesSRT.enqueues.warning.noPresentationSelected'
                                                ),
                                                {
                                                    variant: 'warning'
                                                }
                                            );
                                            return;
                                        }
                                        if (
                                            selectedVisits?.length <= 0 &&
                                            currentTab === TAB_INDEX.VISITS
                                        ) {
                                            enqueueSnackbar(
                                                translate(
                                                    'drawers.generateFilesSRT.enqueues.warning.noVisitsSelected'
                                                ),
                                                {
                                                    variant: 'warning'
                                                }
                                            );
                                            return;
                                        }
                                        setOpen(DRAWER_ACTION_VALUES.GENERATE_FILES_SRT);
                                    }}
                                />
                            )}
                        </Grid>
                        {/* RGRL y Visitas eligen el Cliente en el filtro global; Siniestros, en el
                            primer filtro de su tab */}
                        {isPerfilBackOffice && (
                            <ArchivosGeneradosHoy
                                tipo={TIPO_CONTADOR_POR_TAB[currentTab]}
                                idOrganizacion={
                                    currentTab === TAB_INDEX.SINIESTROS
                                        ? siniestrosTab.clientId
                                        : normalizarIdOrganizacion(selectedOrganization)
                                }
                            />
                        )}
                        </Box>
                    </SectionHeader>
                    <TabPanel value={currentTab} index={TAB_INDEX.RGRL}>
                        <UploadErrorInfoAlert
                            testID={TEST_IDS.errorsInfo}
                            presentationUploadErrors={presentationUploadErrors}
                            translate={translate}
                            establishmentsOptions={establishmentsOptions}
                            contractsOptions={contractsOptions}
                        />
                    </TabPanel>
                    <Grid display="flex" direction={'row'} bgcolor={'#fff'} pb={2} pl={3.5}>
                        <Tabs value={currentTab} onChange={handleTabChange}>
                            <Tab
                                sx={{ fontSize: '18px', fontFamily: 'Roboto, sans-serif' }}
                                label={translate('header.tabs.rgrl')}
                                value={TAB_INDEX.RGRL}
                            />
                            <Tab
                                sx={{ fontSize: '18px', fontFamily: 'Roboto, sans-serif' }}
                                label={translate('header.tabs.visits')}
                                value={TAB_INDEX.VISITS}
                            />
                            <Tab
                                sx={{ fontSize: '18px', fontFamily: 'Roboto, sans-serif' }}
                                label={translate('header.tabs.siniestros')}
                                value={TAB_INDEX.SINIESTROS}
                            />
                        </Tabs>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <TabPanel value={currentTab} index={TAB_INDEX.RGRL}>
                        <SearchToolbar
                            inputSearchName=""
                            onChange={() => {}}
                            onClick={handleSubmitSearch}
                            showSearchInput={false}
                            clearFilters={() => {
                                resetDateRange();
                                clearFilters();
                            }}
                        >
                            <RGRLFilters
                                extraFilters={extraFilters}
                                setFilters={setFilters}
                                handleChangeExtraFilters={handleChangeExtraFilters}
                                contractsOptions={contractsOptions}
                                establishmentsOptions={establishmentsOptions}
                                startDate={draftStartDate}
                                endDate={draftEndDate}
                                handleDateChange={handleDateChange}
                                onOrganizationChange={() => setSelectedPresentations([])}
                            />
                        </SearchToolbar>
                    </TabPanel>
                    <TabPanel value={currentTab} index={TAB_INDEX.VISITS}>
                        <SearchToolbar
                            inputSearchName=""
                            onChange={() => {}}
                            onClick={handleSubmitVisitsSearch}
                            showSearchInput={false}
                            clearFilters={() => {
                                visitsClearFilters();
                            }}
                            showMoreFiltersButton
                            isShowMoreFiltersOpen={isShowMoreVisitsFiltersOpen}
                            handleShowMoreFiltersPress={handleToggleVisitsMoreFilters}
                        >
                            <VisitsFilters
                                extraFilters={visitsExtraFilters}
                                setFilters={setVisitsFilters}
                                handleChangeExtraFilters={handleChangeVisitsExtraFilters}
                                contractsOptions={contractsOptions}
                                isFetchingContractsOptions={isFetchingContractsOptions}
                                establishmentsOptions={establishmentsOptions}
                                isFetchingEstablishmentsOptions={isFetchingEstablishmentsOptions}
                                handleDateChange={() => {}}
                                visitTypesOptions={visitTypesOptions}
                                isFetchingVisitTypes={isFetchingVisitTypes}
                                onOrganizationChange={() => setSelectedVisits([])}
                                isShowMoreFiltersOpen={isShowMoreVisitsFiltersOpen}
                            />
                        </SearchToolbar>
                    </TabPanel>
                    <TabPanel value={currentTab} index={TAB_INDEX.SINIESTROS}>
                        <SearchToolbar
                            onChange={() => {}}
                            onClick={siniestrosTab.handleSubmitSearch}
                            clearFilters={siniestrosTab.handleLimpiarFiltros}
                            showSearchInput={false}
                            showMoreFiltersButton
                            isShowMoreFiltersOpen={isShowMoreSiniestrosFiltersOpen}
                            handleShowMoreFiltersPress={() =>
                                setIsShowMoreSiniestrosFiltersOpen(
                                    (previouslyOpen) => !previouslyOpen
                                )
                            }
                        >
                            <SiniestrosFilters
                                extraFilters={siniestrosTab.extraFilters}
                                handleChangeExtraFilters={siniestrosTab.handleChangeExtraFilters}
                                isShowMoreFiltersOpen={isShowMoreSiniestrosFiltersOpen}
                                activeTab={siniestrosTab.activeTab}
                                setActiveTab={siniestrosTab.setActiveTab}
                                onClientChange={siniestrosTab.handleCambiarCliente}
                                srtStatusOptions={SRT_STATUS_OPTIONS_EXPORTABLES}
                                srtStatusLabel={`${translate('header.extraFilters.stateSRT')}`}
                                ocultarEstadoYPreventor
                            />
                        </SearchToolbar>
                    </TabPanel>
                </Grid>
                <Grid item xs={12}>
                    <TabPanel value={currentTab} index={TAB_INDEX.RGRL}>
                        {presentations?.data?.data?.length === 0 ? (
                            <EmptyTableSection title={translate('table.noData.title')} />
                        ) : (
                            <DataTable
                                testID={TEST_IDS.table.id}
                                columnTitles={columnTitles({
                                    translate,
                                    isSelectAllEnabled,
                                    checked: isAllSelected,
                                    onSelectAllCheckPress: handleSelectAllCheckPress
                                })}
                                columnWidths={[]}
                                onSelectRow={handleRowClick}
                                selected={selected}
                                selectedItemFromAction={selectedItemFromAction}
                                menuOpen={menuOpen}
                                items={presentations || []}
                                loading={isFetchingPresentations}
                                filters={baseFilters}
                                setFilters={setFilters}
                                isFetching={isFetchingPresentations}
                                headers={[
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) => (
                                            <Checkbox
                                                checked={selectedPresentations.some(
                                                    (selectedPresentation: any) =>
                                                        selectedPresentation.id === item.id
                                                )}
                                                disabled={item?.estadoSRT === SRT_STATES.ERROR}
                                                onChange={() => handleItemCheck(item)}
                                            />
                                        )
                                    },
                                    {
                                        type: 'data',
                                        maxWidth: '200px',
                                        upperLabel: (item: any) =>
                                            getPresentationTypeName(item?.tipo?.nombre, translate)
                                    },
                                    {
                                        type: 'data',
                                        maxWidth: '250px',
                                        upperLabel: (item: any) =>
                                            `${item?.establecimiento?.organismo?.contrato?.cuit} - ${item?.establecimiento?.organismo?.contrato?.nombre}`
                                    },
                                    {
                                        type: 'data',
                                        maxWidth: '250px',
                                        onClickColumnTitleSortName: 'codigoSRT',
                                        upperLabel: (item: any) =>
                                            `${item?.establecimiento?.numero} - ${item?.establecimiento?.nombre}`
                                    },
                                    {
                                        type: 'data',
                                        maxWidth: '250px',
                                        upperLabel: (item: any) =>
                                            getDate(item?.fechaPresentacion) || '-'
                                    },
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) =>
                                            renderStateChipSRT(item?.estadoSRT, translate)
                                    },
                                    {
                                        type: 'data',
                                        upperLabel: (item: any) =>
                                            item?.archivos[0]?.fechaGeneracion
                                                ? getDate(item.archivos[0].fechaGeneracion)
                                                : '-'
                                    },
                                    {
                                        type: 'action',
                                        onClick: handleClickAction
                                    }
                                ]}
                            />
                        )}
                    </TabPanel>
                    <TabPanel value={currentTab} index={TAB_INDEX.VISITS}>
                        <VisitsTable
                            items={visitas || []}
                            handleItemCheck={handleVisitItemCheck}
                            selectedVisits={selectedVisits}
                            translate={translate}
                            handleRowClick={handleVisitRowClick}
                            selected={selectedVisitElement}
                            selectedItemFromAction={selectedVisitItemFromAction}
                            menuOpen={visitsTableMenuOpen}
                            filters={visitsBaseFilters}
                            setFilters={setVisitsFilters}
                            isFetching={isFechingVisits}
                            handleClickAction={handleVisitClickAction}
                            selectedVisitType={visitsExtraFilters?.idTipo}
                            isSelectAllEnabled={isVisitsSelectAllEnabled}
                            isAllSelected={isAllVisitsSelected}
                            onSelectAllCheckPress={handleVisitsSelectAllCheckPress}
                        />
                    </TabPanel>
                    <TabPanel value={currentTab} index={TAB_INDEX.SINIESTROS}>
                        {!siniestrosTab.isLoading && siniestrosTab.siniestros.length === 0 ? (
                            <EmptyTableSection title={translate('siniestros.noData')} />
                        ) : (
                            <SiniestrosTable
                                filters={siniestrosTab.filters}
                                setFilters={siniestrosTab.setFilters}
                                siniestros={siniestrosTab.siniestros}
                                isLoading={siniestrosTab.isLoading}
                                count={siniestrosTab.totalCount}
                                canViewDetail={FEATURES.CONSULTA_SINIESTRO_ROAM.hasAccess}
                                canCancel={false}
                                canCreateVisit={false}
                                activeTab={siniestrosTab.activeTab}
                                onRefresh={siniestrosTab.refetch}
                                t={tSiniestros}
                                selectedIds={siniestrosTab.selectedIds}
                                seleccionEstado={siniestrosTab.seleccionEstado}
                                isSeleccionable={isSeleccionableArchivosSrt}
                                handleToggleSeleccion={siniestrosTab.handleToggleSeleccion}
                                handleSelectAll={siniestrosTab.handleSelectAll}
                                handleClearSeleccion={siniestrosTab.handleClearSeleccion}
                                archivosSrt={{
                                    onDescargarTxt: siniestrosTab.handleDescargarTxt,
                                    isDescargando: siniestrosTab.isDescargandoTxt,
                                    detalleState: {
                                        volverA: `/${routePrefix}/gestion-de-archivos-para-la-SRT`
                                    },
                                    onDescargarInforme: siniestrosTab.handleDescargarInforme,
                                    onDescargarActa: siniestrosTab.handleDescargarActa
                                }}
                            />
                        )}
                    </TabPanel>
                </Grid>
                <TabPanel value={currentTab} index={TAB_INDEX.RGRL}>
                    <Menu id="basic-menu" anchorEl={anchorEl} open={menuOpen} onClose={closeMenu}>
                        <Box>
                            {selectedItemFromAction?.estadoSRT === SRT_STATES.ERROR &&
                                selectedItemFromAction?.estadoSRT !== SRT_STATES.GENERATED && (
                                    <MenuItem
                                        data-testid={TEST_IDS.table.actions.editPresentation}
                                        onClick={() => {
                                            const establihsmentData = establishmentsOptions.find(
                                                (establishmentOptions: any) =>
                                                    selectedItemFromAction?.establecimiento?.id ===
                                                    establishmentOptions?.id
                                            );
                                            localStorage.setItem(
                                                'selectedPresentationId',
                                                selectedItemFromAction?.id.toString()
                                            );
                                            navigate(
                                                `/backoffice/establecimiento/presentaciones/editar`,
                                                {
                                                    state: {
                                                        selectedPresentationId:
                                                            selectedItemFromAction?.id,
                                                        selectedEstablishmentId:
                                                            selectedItemFromAction?.establecimiento
                                                                ?.id,
                                                        tipoId: selectedItemFromAction?.tipo?.id,
                                                        tipoTitulo:
                                                            selectedItemFromAction?.tipo?.nombre,
                                                        selectedSRTCode: establihsmentData?.numero
                                                    }
                                                }
                                            );
                                        }}
                                    >
                                        {translate('table.actions.editPresentation')}
                                    </MenuItem>
                                )}
                            {selectedItemFromAction?.estadoSRT !== SRT_STATES.ERROR && (
                                <MenuItem
                                    data-testid={TEST_IDS.table.actions.seePresentation}
                                    onClick={() => {
                                        navigate(
                                            `/backoffice/establecimiento/presentaciones/visualizar`,
                                            {
                                                state: {
                                                    selectedPresentationId:
                                                        selectedItemFromAction?.id,
                                                    selectedEstablishmentId:
                                                        selectedItemFromAction?.establecimiento?.id,
                                                    tipoId: selectedItemFromAction?.tipo?.id,
                                                    tipoTitulo: selectedItemFromAction?.tipo?.nombre
                                                }
                                            }
                                        );
                                    }}
                                >
                                    {translate('table.actions.seePresentation')}
                                </MenuItem>
                            )}
                            <MenuItem
                                data-testid={TEST_IDS.table.actions.downloadPresentationPDF}
                                onClick={handleDownloadPresentation}
                            >
                                {translate('table.actions.downloadPDF')}
                            </MenuItem>
                        </Box>
                    </Menu>
                </TabPanel>
                <TabPanel value={currentTab} index={TAB_INDEX.VISITS}>
                    <ActionsMenu
                        menuItems={visitsTableMenuItems}
                        anchorEl={visitsAnchorEl}
                        menuOpen={visitsTableMenuOpen}
                        closeMenu={visitsCloseMenu}
                    />
                </TabPanel>
            </Grid>
            <DrawerManager
                open={open}
                setOpen={setOpen}
                translate={translate}
                handleCloseDrawer={handleCloseDrawer}
                generateAndDownloadFiles={
                    currentTab === TAB_INDEX.RGRL
                        ? generateAndDownloadFiles
                        : currentTab === TAB_INDEX.VISITS
                          ? generateAndDownloadVisitsFiles
                          : () => {}
                }
                currentTab={currentTab}
            />
            <BackdropComponent
                loading={
                    // isLoadingRgrl ||
                    isDownloading ||
                    isDownloadingPDF ||
                    isDownloading ||
                    isDownloadingVisitActa ||
                    isGeneratingVisitsFiles ||
                    isExportingVisits ||
                    isExportingPresentations ||
                    siniestrosTab.isDescargandoTxt ||
                    siniestrosTab.isDescargandoPdf ||
                    siniestrosTab.isExportandoExcel
                }
                fetching={isGeneretingFiles || siniestrosTab.isGenerandoArchivo}
            />
        </>
    );
};

export default RGRLIndex;
